import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Compass,
  Globe2,
  Network,
  ShieldCheck,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import PageBanner from "../components/PageBanner";

const VISION_TILES = [
  {
    icon: Globe2,
    title: "A connected global future",
    text: "Communities everywhere participating fully in a modern, connected global economy.",
  },
  {
    icon: Network,
    title: "Ecosystems, not silos",
    text: "Health, energy, food, education and digital infrastructure designed to work together.",
  },
  {
    icon: ShieldCheck,
    title: "Trust as infrastructure",
    text: "Blockchain-backed transparency for records, services and cross-border investment.",
  },
  {
    icon: Sparkles,
    title: "Innovation with purpose",
    text: "Practical, scalable solutions that move people from temporary support to lasting resilience.",
  },
];

export default function Vision() {
  return (
    <div data-testid="vision-page">
      <PageBanner
        overline="Our Vision"
        title="A world where every community thrives."
        subtitle="We see a future where healthcare, energy, food, education and digital infrastructure are accessible — and the systems that connect them are trusted, transparent and built to last."
        image="https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/241396fa1857bef64b589bbf9b20a0eac09ec6cd9dedd9f9485275207579f472.png"
      />

      {/* Vision statement */}
      <section className="section">
        <div className="container-tmb grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="flex items-center gap-3 mb-4">
              <Compass className="w-5 h-5 text-[#C85A3A]" />
              <div className="overline">Vision statement</div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Building resilient communities through innovation, access and global collaboration.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7 space-y-5 text-[#4A5D54] text-base md:text-lg leading-relaxed"
          >
            <p>
              TMB Global Foundation envisions a world where underserved communities are
              no longer left behind by progress. We see a future shaped by connected
              ecosystems — where solar-powered clinics, secure portable health records,
              safe food networks, future-ready schools and trusted digital
              infrastructure are part of everyday life.
            </p>
            <p>
              By connecting systems, people and resources, we are creating sustainable
              pathways for communities to thrive in the future — locally led, globally
              supported and built to scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision tiles */}
      <section className="section bg-[#F0EBE1]">
        <div className="container-tmb">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">The future we're building</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Four convictions that guide every decision.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {VISION_TILES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                  className="card-tmb p-8"
                  data-testid={`vision-tile-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#C85A3A]" />
                  </div>
                  <h3 className="font-display text-2xl text-[#1A2421] mb-3">
                    {v.title}
                  </h3>
                  <p className="text-sm text-[#4A5D54] leading-relaxed">{v.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section">
        <div className="container-tmb grid md:grid-cols-2 gap-6">
          <div className="card-tmb p-10 bg-[#2C4C3B] text-white border-transparent">
            <div className="text-[#D99A29] uppercase tracking-[0.2em] font-bold text-xs mb-4">
              The horizon
            </div>
            <p className="font-display text-3xl md:text-4xl leading-tight">
              A future where every community has the systems, skills and trust to thrive
              — generation after generation.
            </p>
          </div>
          <div className="card-tmb p-10">
            <div className="overline mb-4">From vision to action</div>
            <h3 className="font-display text-3xl md:text-4xl text-[#1A2421] leading-tight mb-4">
              Our mission turns this vision into daily work.
            </h3>
            <p className="text-[#4A5D54] leading-relaxed mb-6">
              See exactly how we deliver health access, clean energy, food safety,
              future-ready education and digital trust infrastructure.
            </p>
            <Link to="/mission" data-testid="vision-mission-cta" className="btn-primary">
              See our mission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
